import React, { memo, useMemo, useState, useEffect, useRef } from "react";
import clx from "clsx";
import { News } from "@/types";
import HotNewsBadge from "@/components/ui/HotNewsBadge";
import NewsOverallReaction from "@/components/news/NewsOverallReaction";
import { NewsLink } from "@/components/ui/SmartLink";
import { getImageUrl } from "@/utils/unifiedImageUtils";
import {
  generateLQIPUrl,
  getOptimalLQIP,
  getCachedLQIP,
  LQIP_PRESETS,
  type LQIPPreset,
} from "@/utils/lqipUtils";

/**
 * LazyImageArticleCard - Enhanced version with improved image loading
 * Combines the best of ArticleCard UI with robust image handling
 */

export type ArticleVariant = "hero" | "row" | "compact" | "sidebar";

export interface LazyImageArticleCardProps {
  news: News;
  variant?: ArticleVariant;
  className?: string;
  showAuthor?: boolean;
  showViews?: boolean;
  showReactions?: boolean;
  imageClassName?: string;
  titleClassName?: string;
  metaClassName?: string;
  imageAspect?: `${number}/${number}` | "16/9" | "4/5" | "1/1" | "3/2";
  priorityImage?: boolean;
  prefetchStrategy?: "viewport" | "hover" | "none";
  debug?: boolean;
}

// Smart Image Component with emergency fixes and LQIP
interface SmartImageProps {
  src: string;
  alt: string;
  className?: string;
  aspectRatio?: string;
  priority?: boolean;
  onError?: (e: any) => void;
  debug?: boolean;
  enableLQIP?: boolean;
  lqipPreset?: LQIPPreset;
}

const SmartImage: React.FC<SmartImageProps> = ({
  src,
  alt,
  className,
  aspectRatio = "16/9",
  priority = false,
  onError,
  debug = false,
  enableLQIP = true,
  lqipPreset = "thumbnail",
}) => {
  const [imageState, setImageState] = useState<
    "loading" | "loaded" | "error" | "fallback"
  >("loading");
  const [lqipState, setLqipState] = useState<
    "loading" | "loaded" | "error" | "skipped"
  >("loading");
  const [currentSrc, setCurrentSrc] = useState<string>("");
  const [lqipSrc, setLqipSrc] = useState<string>("");
  const [isVisible, setIsVisible] = useState(priority);
  const [retryCount, setRetryCount] = useState(0);
  const imgRef = useRef<HTMLDivElement>(null);
  const componentId = useRef(
    `smart-img-${Math.random().toString(36).substr(2, 9)}`,
  );

  const debugLog = (message: string, data?: any) => {
    if (debug && process.env.NODE_ENV === "development") {
      console.log(`[SmartImage:${componentId.current}] ${message}`, data || "");
    }
  };

  // Enhanced URL fixing with multiple fallback strategies
  const fallbackUrls = useMemo(() => {
    const baseUrl = window.location.origin;
    const urls = [];

    // Handle empty or invalid src
    if (!src || src.trim() === "") {
      return [`${baseUrl}/img/noimg.jpg`];
    }

    let cleanSrc = src.trim();

    // Fix common URL issues
    if (cleanSrc.includes("http:/localhost")) {
      cleanSrc = cleanSrc.replace("http:/localhost", "http://localhost");
    }

    // 1. Original URL (cleaned)
    urls.push(cleanSrc);

    // 2. If not absolute URL, try with base URL
    if (!cleanSrc.startsWith("http")) {
      if (cleanSrc.startsWith("/")) {
        urls.push(`${baseUrl}${cleanSrc}`);
      } else {
        urls.push(`${baseUrl}/${cleanSrc}`);
      }
    }

    // 3. Try storage path variations
    const filename = cleanSrc.split("/").pop() || "";
    if (filename && !cleanSrc.includes("/storage/")) {
      urls.push(`${baseUrl}/storage/images/${filename}`);
      urls.push(`${baseUrl}/storage/${filename}`);
    }

    // 4. Final fallback
    urls.push(`${baseUrl}/img/noimg.jpg`);

    // Remove duplicates
    return [...new Set(urls)];
  }, [src]);

  // Generate LQIP URL with advanced utilities
  const lqipData = useMemo(() => {
    if (!enableLQIP || !src || src.trim() === "") {
      return { url: "", isGenerated: false };
    }

    try {
      // Use optimal LQIP based on connection speed and preset
      const optimalLQIP = getOptimalLQIP(src);

      // If optimal LQIP failed, try cached version with preset
      if (!optimalLQIP.isGenerated) {
        const cachedLQIP = getCachedLQIP(src, LQIP_PRESETS[lqipPreset]);
        return { url: cachedLQIP.url, isGenerated: cachedLQIP.isGenerated };
      }

      debugLog("LQIP generated", {
        url: optimalLQIP.url,
        config: optimalLQIP.config,
      });
      return { url: optimalLQIP.url, isGenerated: optimalLQIP.isGenerated };
    } catch (error) {
      debugLog("LQIP generation failed", error);
      return { url: "", isGenerated: false };
    }
  }, [src, enableLQIP, lqipPreset, debug]);

  const tryNextUrl = () => {
    if (retryCount < fallbackUrls.length - 1) {
      const nextRetry = retryCount + 1;
      const nextUrl = fallbackUrls[nextRetry];

      debugLog(
        `Trying fallback URL ${nextRetry}/${fallbackUrls.length - 1}`,
        nextUrl,
      );

      setRetryCount(nextRetry);
      setCurrentSrc(nextUrl);
      setImageState("loading");

      if (nextRetry === fallbackUrls.length - 1) {
        setImageState("fallback");
      }
    } else {
      debugLog("All URLs failed, showing error state");
      setImageState("error");
    }
  };

  // Enhanced Intersection Observer with timeout fallback
  useEffect(() => {
    if (priority) {
      debugLog("Priority loading enabled");
      setIsVisible(true);
      setCurrentSrc(fallbackUrls[0]);
      // Load LQIP immediately for priority images
      if (lqipData.url && lqipData.isGenerated) {
        setLqipSrc(lqipData.url);
      } else {
        setLqipState("skipped");
      }
      return;
    }

    if (isVisible) return;

    // Timeout fallback - if not detected in 3 seconds, load anyway
    const fallbackTimer = setTimeout(() => {
      debugLog("Intersection observer timeout, loading image");
      setIsVisible(true);
      setCurrentSrc(fallbackUrls[0]);
    }, 3000);

    try {
      const observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            debugLog("Intersection observed", {
              isIntersecting: entry.isIntersecting,
              intersectionRatio: entry.intersectionRatio,
            });

            if (entry.isIntersecting) {
              clearTimeout(fallbackTimer);
              setIsVisible(true);
              setCurrentSrc(fallbackUrls[0]);
              // Load LQIP when in view
              if (lqipData.url && lqipData.isGenerated) {
                setLqipSrc(lqipData.url);
              } else {
                setLqipState("skipped");
              }
              observer.disconnect();
            }
          });
        },
        {
          threshold: 0.01,
          rootMargin: "50px",
        },
      );

      if (imgRef.current) {
        observer.observe(imgRef.current);
        debugLog("Observer setup successful");
      }

      return () => {
        clearTimeout(fallbackTimer);
        observer.disconnect();
      };
    } catch (error) {
      debugLog("Observer setup failed, loading immediately", error);
      clearTimeout(fallbackTimer);
      setIsVisible(true);
      setCurrentSrc(fallbackUrls[0]);
      // Load LQIP on fallback
      if (lqipData.url && lqipData.isGenerated) {
        setLqipSrc(lqipData.url);
      } else {
        setLqipState("skipped");
      }
    }
  }, [priority, isVisible, fallbackUrls, lqipData, debug]);

  const handleLoad = () => {
    debugLog("Image loaded successfully", currentSrc);
    setImageState("loaded");
  };

  const handleLqipLoad = () => {
    debugLog("LQIP loaded successfully", lqipSrc);
    setLqipState("loaded");
  };

  const handleLqipError = () => {
    debugLog("LQIP failed to load", lqipSrc);
    setLqipState("error");
  };

  const handleError = (e: React.SyntheticEvent<HTMLImageElement>) => {
    debugLog("Image failed to load", {
      src: currentSrc,
      retry: retryCount,
      maxRetries: fallbackUrls.length - 1,
    });

    if (onError) {
      onError(e);
    }

    // Try next URL after small delay
    setTimeout(tryNextUrl, 200);
  };

  const aspectRatioClass =
    {
      "16/9": "aspect-[16/9]",
      "4/5": "aspect-[4/5]",
      "1/1": "aspect-square",
      "3/2": "aspect-[3/2]",
    }[aspectRatio] || "aspect-[16/9]";

  return (
    <div
      ref={imgRef}
      className={clx(
        "relative overflow-hidden bg-gray-100 dark:bg-gray-800",
        aspectRatioClass,
      )}
    >
      {/* Debug overlay */}
      {debug && process.env.NODE_ENV === "development" && (
        <div className="absolute top-0 left-0 bg-black bg-opacity-75 text-white text-xs p-1 z-50 max-w-[200px]">
          <div>ID: {componentId.current.slice(-6)}</div>
          <div>State: {imageState.toUpperCase()}</div>
          <div>LQIP: {lqipState.toUpperCase()}</div>
          <div>
            Retry: {retryCount}/{fallbackUrls.length - 1}
          </div>
          <div>Visible: {isVisible.toString()}</div>
          <div className="truncate">Src: {currentSrc.split("/").pop()}</div>
        </div>
      )}

      {/* Not in view placeholder - removed since LQIP handles this */}

      {/* Loading state - only as fallback when LQIP not available */}
      {isVisible && imageState === "loading" && lqipState === "skipped" && (
        <div className="absolute inset-0 flex items-center justify-center bg-gray-200 dark:bg-gray-700">
          <div className="flex flex-col items-center space-y-2">
            <div className="w-6 h-6 bg-gray-300 dark:bg-gray-600 rounded animate-pulse"></div>
          </div>
        </div>
      )}

      {/* Error state */}
      {imageState === "error" && (
        <div className="absolute inset-0 flex items-center justify-center bg-gray-200 dark:bg-gray-700">
          <div className="flex flex-col items-center space-y-2 text-gray-500">
            <svg
              className="w-6 h-6"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 002 2z"
              />
            </svg>
            <span className="text-xs text-center">
              Gambar tidak
              <br />
              tersedia
            </span>
          </div>
        </div>
      )}

      {/* LQIP Image */}
      {isVisible && lqipSrc && lqipState !== "skipped" && (
        <img
          src={lqipSrc}
          alt={`${alt} (loading)`}
          className={clx(
            "absolute inset-0 w-full h-full object-cover transition-all duration-700 ease-out",
            lqipState === "loaded" && imageState !== "loaded"
              ? "opacity-100 scale-105"
              : "opacity-0 scale-100",
          )}
          onLoad={handleLqipLoad}
          onError={handleLqipError}
          loading="eager"
          style={{
            filter:
              lqipState === "loaded"
                ? "blur(8px) brightness(1.1)"
                : "blur(15px)",
            transform: lqipState === "loaded" ? "scale(1.05)" : "scale(1.1)",
          }}
        />
      )}

      {/* Main Image */}
      {isVisible && currentSrc && (
        <img
          src={currentSrc}
          alt={alt}
          className={clx(
            "absolute inset-0 w-full h-full object-cover transition-all duration-700 ease-in",
            imageState === "loaded"
              ? "opacity-100 scale-100"
              : "opacity-0 scale-105",
            className,
          )}
          onLoad={handleLoad}
          onError={handleError}
          loading={priority ? "eager" : "lazy"}
        />
      )}
    </div>
  );
};

// Utility functions
const formatDate = (dateStr?: string) => {
  if (!dateStr) return "";
  try {
    return new Date(dateStr).toLocaleDateString("id-ID", {
      day: "numeric",
      month: "short",
      year: "numeric",
    });
  } catch {
    return "";
  }
};

const BaseMeta: React.FC<{
  news: News;
  className?: string;
  showAuthor?: boolean;
  showViews?: boolean;
  showReactions?: boolean;
}> = ({
  news,
  className,
  showAuthor = true,
  showViews = true,
  showReactions = false,
}) => {
    const dateLabel = useMemo(
      () => formatDate(news.created_at),
      [news.created_at],
    );

    return (
      <div
        className={clx(
          "flex flex-col lg:flex-row lg:items-center text-xs text-gray-500 dark:text-gray-400 gap-2",
          className,
        )}
      >
        <div className="flex items-center gap-2">
          {dateLabel && <span>{dateLabel}</span>}
          {/* {showViews && (
            <span className="flex items-center gap-1">
              <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                <path d="M10 12a2 2 0 100-4 2 2 0 000 4z" />
                <path
                  fillRule="evenodd"
                  d="M.458 10C1.732 5.943 5.522 3 10 3s8.268 2.943 9.542 7c-1.274 4.057-5.064 7-9.542 7S1.732 14.057.458 10zM14 10a4 4 0 11-8 0 4 4 0 018 0z"
                  clipRule="evenodd"
                />
              </svg>
              {news.views}
            </span>
          )} */}
        </div>

        {showAuthor && (
          <span className="md:order-first">
            Oleh <b>{news.author?.username || "Anonim"}</b>
          </span>
        )}
        {showReactions && (
          <NewsOverallReaction reactions={news.reactions?.counts} size="sm" />
        )}
      </div>
    );
  };

// Hero Card
const HeroCard: React.FC<LazyImageArticleCardProps> = ({
  news,
  className,
  showAuthor = true,
  showViews = true,
  showReactions = true,
  imageClassName,
  titleClassName,
  metaClassName,
  imageAspect = "16/9",
  priorityImage,
  prefetchStrategy = "viewport",
  debug = false,
}) => {
  return (
    <article className={clx("flex flex-col", className)}>
      <NewsLink
        newsSlug={news.slug || `news-${news.id}`}
        prefetchStrategy={prefetchStrategy}
        className="rounded-xl relative overflow-hidden flex-none border border-gray-200/40 w-full"
      >
        <SmartImage
          src={getImageUrl(news)}
          alt={news.title}
          className={imageClassName}
          aspectRatio={imageAspect}
          priority={!!priorityImage}
          debug={debug}
          enableLQIP={!priorityImage} // LQIP for non-priority images
          lqipPreset="hero"
        />
        <HotNewsBadge isHot={news.isHotnews} size="md" />
      </NewsLink>

      <div className="flex flex-col py-3">
        {showAuthor && (
          <span>
            Oleh <b>{news.author?.username || "Anonim"}</b>
          </span>
        )}
        <NewsLink
          newsSlug={news.slug || `news-${news.id}`}
          prefetchStrategy="hover"
          className={clx(
            "font-bold text-gray-900 dark:text-white line-clamp-2 hover:underline",
            titleClassName || "text-lg",
          )}
        >
          {news.title}
        </NewsLink>
        <BaseMeta
          news={news}
          className={clx("mt-2 gap-4", metaClassName)}
          showAuthor={false}
          showViews={showViews}
          showReactions={showReactions}
        />
      </div>
    </article>
  );
};

// Row Item
const RowItem: React.FC<LazyImageArticleCardProps> = ({
  news,
  className,
  titleClassName,
  metaClassName,
  showAuthor = true,
  showViews = true,
}) => {
  return (
    <div
      className={clx(
        "transition-all border-b border-gray-200 dark:border-gray-700 px-4 pt-2 pb-3",
        "hover:rounded-lg hover:bg-[#de0b66]/5 hover:border-gray-400/20 hover:border",
        "hover:dark:bg-red-700/20 hover:dark:border-gray-400/50 hover:pt-4 hover:pb-4",
        className,
      )}
    >
      <NewsLink
        newsSlug={news.slug || `news-${news.id}`}
        prefetchStrategy="hover"
      >
        <h4
          className={clx(
            "font-semibold text-gray-900 dark:text-white mb-1",
            titleClassName || "text-sm",
          )}
        >
          {news.title}
        </h4>
      </NewsLink>
      <BaseMeta
        news={news}
        className={metaClassName}
        showAuthor={showAuthor}
        showViews={showViews}
        showReactions
      />
    </div>
  );
};

// Compact Card
const CompactCard: React.FC<LazyImageArticleCardProps> = ({
  news,
  className,
  imageClassName,
  titleClassName,
  metaClassName,
  showViews = true,
  imageAspect = "16/9",
  debug = false,
}) => {
  return (
    <div
      className={clx(
        "flex gap-3 pb-3 border-b md:border-0 border-gray-200 dark:border-gray-700 last:border-b-0",
        className,
      )}
    >
      <div className="w-28 lg:w-24 h-12 flex-shrink-0 relative">
        <SmartImage
          src={getImageUrl(news)}
          alt={news.title}
          className={clx("rounded border border-gray-200/40", imageClassName)}
          aspectRatio={imageAspect}
          debug={debug}
          enableLQIP={true}
          lqipPreset="thumbnail"
        />
        <HotNewsBadge
          isHot={news.isHotnews}
          size="xs"
          className="scale-75 absolute -top-1 -left-1"
        />
      </div>
      <div className="flex-1">
        <NewsLink
          newsSlug={news.slug || `news-${news.id}`}
          prefetchStrategy="hover"
        >
          <h4
            className={clx(
              "font-medium text-gray-900 dark:text-white hover:text-[#de0b66] transition-colors line-clamp-2 mb-1",
              titleClassName || "text-sm",
            )}
          >
            {news.title}
          </h4>
        </NewsLink>
        <BaseMeta
          news={news}
          className={clx(metaClassName)}
          showAuthor
          showViews={showViews}
          showReactions
        />
      </div>
    </div>
  );
};

// Sidebar Mini
const SidebarMini: React.FC<LazyImageArticleCardProps> = ({
  news,
  className,
  titleClassName,
  metaClassName,
}) => {
  return (
    <div
      className={clx(
        "transition-all border-b border-gray-200 dark:border-gray-700 pt-2 pb-3",
        "hover:rounded-lg hover:bg-[#de0b66]/5 hover:border-gray-400/20 hover:border",
        "hover:dark:bg-red-700/20 hover:dark:border-gray-400/50 hover:pt-4 hover:pb-4",
        className,
      )}
    >
      <NewsLink
        newsSlug={news.slug || `news-${news.id}`}
        prefetchStrategy="hover"
      >
        <h4
          className={clx(
            "font-semibold text-gray-900 dark:text-white mb-1",
            titleClassName || "text-sm",
          )}
        >
          {news.title}
        </h4>
      </NewsLink>
      <BaseMeta
        news={news}
        className={metaClassName}
        showAuthor
        showViews
        showReactions
      />
    </div>
  );
};

// Main Component
const LazyImageArticleCardCmp: React.FC<LazyImageArticleCardProps> = (
  props,
) => {
  const { variant = "row" } = props;

  switch (variant) {
    case "hero":
      return <HeroCard {...props} />;
    case "compact":
      return <CompactCard {...props} />;
    case "sidebar":
      return <SidebarMini {...props} />;
    case "row":
    default:
      return <RowItem {...props} />;
  }
};

// Memoization
const areEqual = (
  prev: LazyImageArticleCardProps,
  next: LazyImageArticleCardProps,
) => {
  if (prev.news?.id !== next.news?.id) return false;
  if (prev.variant !== next.variant) return false;
  if (prev.showAuthor !== next.showAuthor) return false;
  if (prev.showViews !== next.showViews) return false;
  if (prev.showReactions !== next.showReactions) return false;
  if (prev.prefetchStrategy !== next.prefetchStrategy) return false;
  if (prev.titleClassName !== next.titleClassName) return false;
  if (prev.metaClassName !== next.metaClassName) return false;
  if (prev.imageClassName !== next.imageClassName) return false;
  if (prev.imageAspect !== next.imageAspect) return false;
  if (prev.priorityImage !== next.priorityImage) return false;
  if (prev.debug !== next.debug) return false;
  return true;
};

const LazyImageArticleCard = memo(LazyImageArticleCardCmp, areEqual);
export default LazyImageArticleCard;

/**
 * ✨ ENHANCED FEATURES:
 *
 * 🔧 Emergency Fixes:
 * - ✅ Empty src prevention
 * - ✅ URL malformed fixing (http:/localhost → http://localhost)
 * - ✅ Multiple fallback URL strategies
 * - ✅ Intersection Observer timeout (3s fallback)
 * - ✅ Smart error handling
 *
 * 🎯 Image Loading:
 * - ✅ Progressive loading with placeholders
 * - ✅ Automatic fallback to noimg.jpg
 * - ✅ Debug overlay (when debug=true)
 * - ✅ Retry mechanism with different URLs
 * - ✅ Priority loading for above-fold images
 *
 * 🎨 UI/UX:
 * - ✅ Same exact styling as original ArticleCard
 * - ✅ Responsive design maintained
 * - ✅ Dark mode support
 * - ✅ Smooth transitions
 * - ✅ Loading states and error states
 *
 * USAGE:
 * <LazyImageArticleCard
 *   news={news}
 *   variant="hero"
 *   priorityImage={true}
 *   debug={true} // Enable during development
 * />
 */
